// ============================================================
// ExpensesTab.jsx  —  Noble Enterprises (tape module)
//
// THE MONTH IS THE AXIS
// Rent, electricity and salaries land every month, so the
// question is always "what did this month cost, and is
// anything higher than last month". A long date range answers
// neither — it averages away the thing worth seeing. The tab
// opens on the current month and steps backwards from there.
//
// A custom range is still there behind a toggle for the wider
// look, but it is the exception, not the default. In range
// mode the month comparison is meaningless and is dropped.
//
// TWO FETCHES, NOT ONE
// The selected month and the one before it, in parallel. The
// backend already returns totals and a category split for any
// window, so the comparison costs one extra request and no
// backend change at all.
//
// TWO KINDS OF ROW
// Manual entries are typed here. Rows with source='hr' were
// posted by the HR module when a salary was allocated to this
// business, and are read-only — editing them here would put
// the two modules out of step. They are shown because they
// are a real cost of running the tape business and the P&L
// would understate without them.
//
// WHAT IS NOT HERE
// Inward freight, builty and labour. Those are allocated onto
// the jumbo rolls at receipt and travel into COGS through
// cost_per_yard. Entering them here too would deduct the same
// rupee twice and nothing on screen would look wrong.
// ============================================================

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import {
    Plus, Pencil, Trash2, X, Check, Lock,
    ChevronLeft, ChevronRight, ChevronDown, Calendar, Truck,
} from 'lucide-react';
import WindowModal, { DEFAULT_FROM_DATE, todayISO } from '../components/WindowModal';
import PrintButton from '../print/PrintButton';
import { expenseReportConfig } from './reports/expenseReport.config';

const API = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const C = {
  brand:      '#f39c12',
  text:       '#1a2332',
  textSoft:   '#64748b',
  textMuted:  '#94a3b8',
  border:     '#e2e8f0',
  borderSoft: '#f1f5f9',
  card:       '#ffffff',
  rowAlt:     '#fafbfc',
  danger:     '#dc2626',
  dangerBg:   '#fee2e2',
  success:    '#059669',
  successBg:  '#d1fae5',
  info:       '#0284c7',
  infoBg:     '#e0f2fe',
};

// Whole rupees on screen. Stored amounts stay exact.
const money = (n) =>
  `Rs ${Math.round(Number(n || 0)).toLocaleString('en-PK')}`;

const fmtDate = (d) => new Date(d).toLocaleDateString('en-GB');


// ------------------------------------------------------------
// MONTH ARITHMETIC
// Months are 'YYYY-MM' strings — what <input type="month">
// speaks, and sortable as text. Built with the Date
// constructor so month 13 rolls into January without a
// special case.
// ------------------------------------------------------------

const pad = (n) => String(n).padStart(2, '0');

const thisMonth = () => {
  const d = new Date();
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}`;
};

const shiftMonth = (m, delta) => {
  const [y, mo] = m.split('-').map(Number);
  const d = new Date(y, mo - 1 + delta, 1);
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}`;
};

const monthStart = (m) => `${m}-01`;

// Day 0 of the next month is the last day of this one.
const monthEnd = (m) => {
  const [y, mo] = m.split('-').map(Number);
  return `${m}-${pad(new Date(y, mo, 0).getDate())}`;
};

const monthLabel = (m) => {
  const [y, mo] = m.split('-').map(Number);
  return new Date(y, mo - 1, 1)
    .toLocaleDateString('en-GB', { month: 'long', year: 'numeric' });
};


// ============================================================
// SHARED UI
// ============================================================

const inputStyle = {
  width: '100%', padding: '10px 13px',
  border: `1px solid ${C.border}`, borderRadius: '8px',
  fontSize: '14px', color: C.text, boxSizing: 'border-box',
  fontFamily: 'inherit', background: '#ffffff',
};

const Field = ({ label, required, children }) => (
  <div>
    <label style={{
      display: 'block', fontSize: '12px', fontWeight: '600',
      color: C.textSoft, marginBottom: '6px',
    }}>
      {label}
      {required && <span style={{ color: C.danger, marginLeft: '3px' }}>*</span>}
    </label>
    {children}
  </div>
);

const TextInput = (props) => <input style={inputStyle} {...props} />;

const Btn = ({ variant = 'primary', children, ...props }) => {
  const styles = {
    primary: { background: C.brand,   color: '#ffffff',  border: 'none' },
    ghost:   { background: '#ffffff', color: C.textSoft, border: `1px solid ${C.border}` },
  };
  return (
    <button
      style={{
        ...styles[variant],
        padding: '10px 18px', borderRadius: '8px', fontSize: '14px',
        fontWeight: '600', cursor: 'pointer', fontFamily: 'inherit',
        display: 'inline-flex', alignItems: 'center', gap: '7px',
      }}
      {...props}
    >
      {children}
    </button>
  );
};

const IconBtn = ({ danger, disabled, title, children, ...props }) => (
  <button
    title={title}
    disabled={disabled}
    style={{
      width: '32px', height: '32px', borderRadius: '7px',
      border: `1px solid ${C.border}`, background: '#ffffff',
      color: disabled ? C.textMuted : (danger ? C.danger : C.textSoft),
      cursor: disabled ? 'not-allowed' : 'pointer',
      opacity: disabled ? 0.45 : 1,
      display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
    }}
    {...props}
  >
    {children}
  </button>
);

const Banner = ({ type, message, onClose }) => {
  if (!message) return null;
  const isError = type === 'error';
  return (
    <div style={{
      background: isError ? C.dangerBg : C.successBg,
      color:      isError ? C.danger   : C.success,
      padding: '11px 16px', borderRadius: '8px', marginBottom: '16px',
      fontSize: '14px', display: 'flex', alignItems: 'center', gap: '10px',
    }}>
      <span style={{ flex: 1 }}>{message}</span>
      <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'inherit', display: 'flex' }}>
        <X size={16} />
      </button>
    </div>
  );
};

const thStyle = {
  textAlign: 'left', padding: '11px 20px', fontSize: '12px',
  fontWeight: '600', color: C.textSoft, background: C.rowAlt,
  textTransform: 'uppercase', letterSpacing: '0.3px',
};
const tdStyle = {
  padding: '13px 20px', fontSize: '14px', color: C.text,
  borderTop: `1px solid ${C.borderSoft}`,
};

const StatCard = ({ label, value, sub, colour = C.text }) => (
  <div style={{
    background: C.card, border: `1px solid ${C.border}`,
    borderRadius: '10px', padding: '16px 18px',
  }}>
    <div style={{ fontSize: '11px', color: C.textMuted, fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.3px' }}>
      {label}
    </div>
    <div style={{ fontSize: '21px', fontWeight: '700', color: colour, marginTop: '4px' }}>
      {value}
    </div>
    <div style={{ fontSize: '11px', color: C.textMuted, marginTop: '2px', minHeight: '14px' }}>
      {sub}
    </div>
  </div>
);


// ============================================================
// PERIOD PICKER
//
// Arrows are the fast path — the owner is nearly always
// looking at this month or the one before. The month input
// itself is there for a jump further back. Forward is blocked
// at the current month; there is nothing to see in the future.
//
// Custom range draft values are held separately and only
// applied on the button, so typing a date doesn't fire a
// request per keystroke.
// ============================================================

const PeriodPicker = ({ view, setView, draft, setDraft, onApplyRange }) => {
  const atCurrent = view.month >= thisMonth();

  if (view.mode === 'range') {
    return (
      <div style={{
        display: 'flex', alignItems: 'flex-end', gap: '12px', flexWrap: 'wrap',
        background: C.rowAlt, border: `1px solid ${C.border}`,
        borderRadius: '10px', padding: '14px 16px', marginBottom: '16px',
      }}>
        <div style={{ width: '150px' }}>
          <Field label="From">
            <TextInput
              type="date"
              value={draft.from_date}
              onChange={e => setDraft({ ...draft, from_date: e.target.value })}
            />
          </Field>
        </div>
        <div style={{ width: '150px' }}>
          <Field label="To">
            <TextInput
              type="date"
              max={todayISO()}
              value={draft.to_date}
              onChange={e => setDraft({ ...draft, to_date: e.target.value })}
            />
          </Field>
        </div>
        <Btn onClick={onApplyRange}>Apply</Btn>
        <Btn
          variant="ghost"
          onClick={() => setView({ ...view, mode: 'month' })}
        >
          Back to months
        </Btn>
      </div>
    );
  }

  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: '10px',
      flexWrap: 'wrap', marginBottom: '16px',
    }}>
      <IconBtn
        title="Previous month"
        onClick={() => setView({ ...view, month: shiftMonth(view.month, -1) })}
      >
        <ChevronLeft size={16} />
      </IconBtn>

      <div style={{
        fontSize: '16px', fontWeight: '700', color: C.text,
        minWidth: '150px', textAlign: 'center',
      }}>
        {monthLabel(view.month)}
      </div>

      <IconBtn
        title={atCurrent ? 'This is the current month' : 'Next month'}
        disabled={atCurrent}
        onClick={() => setView({ ...view, month: shiftMonth(view.month, 1) })}
      >
        <ChevronRight size={16} />
      </IconBtn>

      <input
        type="month"
        max={thisMonth()}
        value={view.month}
        onChange={e => e.target.value && setView({ ...view, month: e.target.value })}
        style={{ ...inputStyle, width: '150px' }}
      />

      <div style={{ marginLeft: 'auto' }}>
        <Btn variant="ghost" onClick={() => setView({ ...view, mode: 'range' })}>
          <Calendar size={16} /> Custom range
        </Btn>
      </div>
    </div>
  );
};


// ============================================================
// WHERE IT WENT
//
// One total tells you what was spent. The split tells you
// whether that is normal — electricity doubling matters, and
// the combined figure hides it. Ranked biggest first, because
// that is the order the owner reads it in.
// ============================================================

const WhereItWent = ({ rows, total, periodLabel }) => {
  if (!rows || rows.length === 0) return null;

  const entries = rows.reduce((s, r) => s + Number(r.entry_count || 0), 0);
  const share = (n) => (total > 0 ? `${Math.round((Number(n) / total) * 100)}%` : '—');

  return (
    <div style={{
      background: C.card, border: `1px solid ${C.border}`,
      borderRadius: '12px', overflow: 'hidden', marginBottom: '16px',
    }}>
      <div style={{ padding: '16px 20px', borderBottom: `1px solid ${C.borderSoft}` }}>
        <div style={{
          fontSize: '12px', fontWeight: '600', color: C.textSoft,
          textTransform: 'uppercase', letterSpacing: '0.3px',
        }}>
          Where it went
        </div>
        <div style={{ fontSize: '13px', color: C.textMuted, marginTop: '3px' }}>
          {periodLabel}
        </div>
      </div>

      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead>
          <tr>
            <th style={thStyle}>Category</th>
            <th style={{ ...thStyle, textAlign: 'right' }}>Entries</th>
            <th style={{ ...thStyle, textAlign: 'right' }}>Amount</th>
            <th style={{ ...thStyle, textAlign: 'right' }}>Share</th>
          </tr>
        </thead>
        <tbody>
          {rows.map(r => (
            <tr key={r.category_name}>
              <td style={{ ...tdStyle, fontWeight: '600' }}>{r.category_name}</td>
              <td style={{ ...tdStyle, textAlign: 'right', color: C.textSoft }}>{r.entry_count}</td>
              <td style={{ ...tdStyle, textAlign: 'right', fontWeight: '600' }}>{money(r.total)}</td>
              <td style={{ ...tdStyle, textAlign: 'right', color: C.textSoft }}>{share(r.total)}</td>
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr>
            <td style={{ ...tdStyle, background: C.rowAlt, color: C.textSoft, fontWeight: '600' }}>
              Total
            </td>
            <td style={{ ...tdStyle, background: C.rowAlt, textAlign: 'right', color: C.textSoft }}>
              {entries}
            </td>
            <td style={{ ...tdStyle, background: C.rowAlt, textAlign: 'right', fontWeight: '700', fontSize: '15px' }}>
              {money(total)}
            </td>
            <td style={{ ...tdStyle, background: C.rowAlt, textAlign: 'right', color: C.textSoft }}>
              {total > 0 ? '100%' : '—'}
            </td>
          </tr>
        </tfoot>
      </table>
    </div>
  );
};




// ============================================================
// EXPENSE FORM
//
// In a window rather than inline, matching the rest of the
// module — the list stays visible behind it and a
// half-finished entry can be minimised.
// ============================================================

const emptyForm = () => ({
  id: null,
  category_id: '',
  expense_date: todayISO(),
  amount: '',
  paid_to: '',
    ref_no: '',
  notes: '',
  location: '',
});

const ExpenseForm = ({ form, setForm, categories, onSave, onCancel, saving }) => (
  <WindowModal
    id="expense-form"
    title={form.id ? 'Edit expense' : 'Record expense'}
    subtitle="Running costs only. Inward freight and labour belong on the receipt."
    onClose={onCancel}
    width="760px"
  >
    <div style={{
      display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(190px, 1fr))',
      gap: '16px', marginBottom: '18px',
    }}>
      <Field label="Category" required>
        <select
          style={inputStyle}
          value={form.category_id}
          onChange={e => setForm({ ...form, category_id: e.target.value })}
        >
          <option value="">Select…</option>
          {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
      </Field>

      <Field label="Location">
        <select
          style={inputStyle}
          value={form.location}
          onChange={e => setForm({ ...form, location: e.target.value })}
        >
          <option value="">Not set</option>
          <option value="factory">Factory</option>
          <option value="shop">Shop</option>
        </select>
      </Field>

      <Field label="Date" required>
        <TextInput
          type="date"
          max={todayISO()}
          value={form.expense_date}
          onChange={e => setForm({ ...form, expense_date: e.target.value })}
        />
      </Field>

      <Field label="Amount" required>
        <TextInput
          type="number" step="0.01" min="0"
          value={form.amount}
          onChange={e => setForm({ ...form, amount: e.target.value })}
          placeholder="0"
        />
      </Field>

      <Field label="Paid to">
        <TextInput
          value={form.paid_to}
          onChange={e => setForm({ ...form, paid_to: e.target.value })}
          placeholder="Who was paid"
        />
      </Field>

      <Field label="Reference">
        <TextInput
          value={form.ref_no}
          onChange={e => setForm({ ...form, ref_no: e.target.value })}
          placeholder="Bill / cheque no."
        />
      </Field>

      <Field label="Notes">
        <TextInput
          value={form.notes}
          onChange={e => setForm({ ...form, notes: e.target.value })}
          placeholder="Optional"
        />
      </Field>
    </div>

    <div style={{ display: 'flex', gap: '10px' }}>
      <Btn onClick={onSave} disabled={saving}>
        <Check size={16} /> {saving ? 'Saving…' : (form.id ? 'Update expense' : 'Save expense')}
      </Btn>
      <Btn variant="ghost" onClick={onCancel}>Cancel</Btn>
    </div>
  </WindowModal>
);


// ============================================================
// MAIN COMPONENT
// ============================================================

const emptyData = { rows: [], totals: {}, by_category: [] };

const ExpensesTab = () => {
    const [data, setData]       = useState(emptyData);
  const [prevData, setPrev]   = useState(null);   // null = no comparison
  const [charges, setCharges] = useState(null);
  const [categories, setCats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm]       = useState(null);
  const [saving, setSaving]   = useState(false);
  const [banner, setBanner]   = useState({ type: '', message: '' });

  const [view, setView] = useState({ mode: 'month', month: thisMonth() });
  const [range, setRange] = useState({ from_date: DEFAULT_FROM_DATE, to_date: todayISO() });
  const [draft, setDraft] = useState({ from_date: DEFAULT_FROM_DATE, to_date: todayISO() });
    const [categoryId, setCategoryId] = useState('');
  const [showInward, setShowInward] = useState(false);

  const headers = { Authorization: `Bearer ${localStorage.getItem('token')}` };

  const notify = (type, message) => {
    setBanner({ type, message });
    if (type === 'success') setTimeout(() => setBanner({ type: '', message: '' }), 3000);
  };

  // The window every query in this tab runs against. Derived,
  // never stored — mode and month are the only truth.
  const window_ = useMemo(() => (
    view.mode === 'month'
      ? { from_date: monthStart(view.month), to_date: monthEnd(view.month) }
      : { from_date: range.from_date, to_date: range.to_date }
  ), [view.mode, view.month, range.from_date, range.to_date]);

  const fetchWindow = useCallback(async (from_date, to_date) => {
    const params = new URLSearchParams();
    if (from_date)  params.append('from_date', from_date);
    if (to_date)    params.append('to_date', to_date);
    if (categoryId) params.append('category_id', categoryId);

    const res = await axios.get(`${API}/tape/finance/expenses?${params}`, { headers });
    return res.data.data || emptyData;
  }, [categoryId]); // eslint-disable-line

  const load = useCallback(async () => {
    setLoading(true);
    try {
      // The previous month is fetched with the same category
      // filter, otherwise the comparison would be between two
      // different questions.
      const wantsPrev = view.mode === 'month';
      const prevMonth = wantsPrev ? shiftMonth(view.month, -1) : null;

            // Inward charges take the window but never the category
      // filter — they are not categorised expenses.
      const chargeParams = new URLSearchParams({
        from_date: window_.from_date,
        to_date:   window_.to_date,
      });

      const [current, previous, cats, chg] = await Promise.all([
        fetchWindow(window_.from_date, window_.to_date),
        wantsPrev
          ? fetchWindow(monthStart(prevMonth), monthEnd(prevMonth))
          : Promise.resolve(null),
        axios.get(`${API}/tape/finance/categories`, { headers }),
        axios.get(`${API}/tape/finance/inward-charges?${chargeParams}`, { headers }),
      ]);

      setData(current);
      setPrev(previous);
      setCats(cats.data.data || []);
      setCharges(chg.data.data || null);
    } catch {
      notify('error', 'Could not load expenses. Is the backend running?');
    } finally {
      setLoading(false);
    }
  }, [view.mode, view.month, window_, fetchWindow]); // eslint-disable-line

  useEffect(() => { load(); }, [load]);

  const applyRange = () => {
    if (!draft.from_date || !draft.to_date) {
      return notify('error', 'Both dates are required');
    }
    if (draft.from_date > draft.to_date) {
      return notify('error', 'From date is after the to date');
    }
    setRange({ ...draft });
  };

  const openEdit = (row) => setForm({
    id: row.id,
    category_id: String(row.category_id),
    expense_date: String(row.expense_date).split('T')[0],
    amount: row.amount,
    paid_to: row.paid_to || '',
    ref_no: row.ref_no || '',
    notes: row.notes || '',
    location: row.location || '',
  });

  const save = async () => {
    const f = form;
    if (!f.category_id)          return notify('error', 'Pick a category');
    if (!f.expense_date)         return notify('error', 'Date is required');
    if (!(Number(f.amount) > 0)) return notify('error', 'Amount must be greater than 0');

    setSaving(true);
    try {
      const payload = {
        category_id:  f.category_id,
        expense_date: f.expense_date,
        amount:       f.amount,
        paid_to:      f.paid_to,
        ref_no:       f.ref_no,
                notes:        f.notes,
        location:     f.location || null,
      };

      if (f.id) {
        await axios.put(`${API}/tape/finance/expenses/${f.id}`, payload, { headers });
        notify('success', 'Expense updated');
      } else {
        await axios.post(`${API}/tape/finance/expenses`, payload, { headers });
        notify('success', 'Expense recorded');
      }

      setForm(null);
      load();
    } catch (err) {
      notify('error', err.response?.data?.message || 'Could not save');
    } finally {
      setSaving(false);
    }
  };

  const remove = async (row) => {
    if (!window.confirm(`Delete this ${money(row.amount)} expense?`)) return;
    try {
      await axios.delete(`${API}/tape/finance/expenses/${row.id}`, { headers });
      notify('success', 'Expense deleted');
      load();
    } catch (err) {
      notify('error', err.response?.data?.message || 'Could not delete');
    }
  };

  // --- the numbers on the cards ---
  const total     = Number(data.totals?.total || 0);
  const entries   = Number(data.totals?.entry_count || 0);
  const biggest   = data.by_category?.[0];
  const prevTotal = prevData ? Number(prevData.totals?.total || 0) : null;

  const periodLabel = view.mode === 'month'
    ? monthLabel(view.month)
    : `${fmtDate(window_.from_date)} — ${fmtDate(window_.to_date)}`;

  // Spending more is the thing to notice, so up is red. Only
  // the delta is coloured; the amount itself never is.
  const comparison = () => {
    if (prevTotal === null) {
      return { value: entries, sub: entries === 1 ? 'entry in this range' : 'entries in this range', colour: C.text, label: 'Entries' };
    }
    if (prevTotal === 0 && total === 0) {
      return { value: '—', sub: 'No spend in either month', colour: C.text, label: 'vs last month' };
    }
    if (prevTotal === 0) {
      return { value: `${money(total)} new`, sub: 'Nothing recorded last month', colour: C.text, label: 'vs last month' };
    }
    const delta = total - prevTotal;
    const pct   = Math.round(Math.abs(delta) / prevTotal * 100);
    if (delta === 0) {
      return { value: 'No change', sub: `${money(prevTotal)} last month`, colour: C.text, label: 'vs last month' };
    }
    return {
      label:  'vs last month',
      value:  `${delta > 0 ? '+' : '−'}${money(Math.abs(delta))} (${delta > 0 ? '+' : '−'}${pct}%)`,
      sub:    `${money(prevTotal)} last month`,
      colour: delta > 0 ? C.danger : C.success,
    };
  };

    const cmp = comparison();

  // --- one list, two totals ---
  //
  // Running costs and inward charges sit in the same table
  // because the owner asks "what did we spend this month" and
  // means both. They are never added together: the first is
  // deducted below gross profit, the second is already inside
  // COGS. One merged total would be a number that appears
  // nowhere in the P&L.
  const chargeTotal = Number(charges?.totals?.total || 0);

  const chargeRows = (charges?.rows || []).map((r, i) => ({
    key:           `chg-${r.inward_id}-${r.charge_type}-${i}`,
    kind:          'inward',
    date:          r.charge_date,
    category_name: `${r.charge_type} (inward)`,
    paid_to:       r.vendor_name,
    ref_no:        [r.bill_number, r.po_number].filter(Boolean).join(' · '),
    notes:         r.landed_on,
    amount:        r.amount,
  }));

    // What the folded row shows without being opened. With one
  // charge it names the receipt outright; with several it says
  // how many, since one line cannot list them all.
  const uniq = (list) => [...new Set(list.filter(Boolean))];
  const chargeVendors = uniq(chargeRows.map(r => r.paid_to));
  const chargeRefs    = uniq(chargeRows.map(r => r.ref_no));
  const chargeDates   = chargeRows.map(r => new Date(r.date)).sort((a, b) => b - a);

  const chargeSummary = {
    date:    chargeDates[0] || null,
    sameDay: chargeDates.length > 0
             && String(chargeDates[0]) === String(chargeDates[chargeDates.length - 1]),
    vendor:  chargeVendors.length === 1 ? chargeVendors[0]
           : `${chargeVendors.length} vendors`,
    ref:     chargeRefs.length === 1 ? chargeRefs[0]
           : `${chargeRefs.length} receipts`,
  };

  const expenseRows = (data.rows || []).map(r => ({
    ...r,
    key:  `exp-${r.id}`,
        // manual = typed here; hr = posted by HR; anything else was
    // posted by the system itself (e.g. roll_close write-off).
    kind: r.source === 'manual' ? 'manual' : r.source === 'hr' ? 'hr' : 'system',
    date: r.expense_date,
  }));

  // 'inward' shows only charges, a real category id shows only
  // expenses — the API already filtered those.
  const visibleRows = (
    categoryId === 'inward' ? chargeRows
    : categoryId           ? expenseRows
        : expenseRows
  ).sort((a, b) => new Date(b.date) - new Date(a.date));

    // Inward charges fold away under one clickable line when all
  // categories are shown. Picking "Inward charges" opens them
  // directly. The two totals appear only when opened.
  // Opened charges render as one block under their header row,
  // not shuffled in among the running costs by date.
  const groupedCharges = (!categoryId && showInward)
    ? [...chargeRows].sort((a, b) => new Date(b.date) - new Date(a.date))
    : [];
  const inwardOpen  = categoryId === 'inward' || (!categoryId && showInward);
  const showRunning = categoryId !== 'inward';
  const showCharges = !categoryId || categoryId === 'inward';
  const showToggle  = !categoryId && chargeTotal > 0;

  return (
    <div>
      <Banner
        type={banner.type}
        message={banner.message}
        onClose={() => setBanner({ type: '', message: '' })}
      />

      <PeriodPicker
        view={view}
        setView={setView}
        draft={draft}
        setDraft={setDraft}
        onApplyRange={applyRange}
      />

      <div style={{
        display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(190px, 1fr))',
        gap: '14px', marginBottom: '16px',
      }}>
        <StatCard
                   label={view.mode === 'month' ? 'Spent this month' : 'Spent in range'}
          value={money(total)}
          sub={
            chargeTotal > 0
              ? `${entries} ${entries === 1 ? 'entry' : 'entries'} · plus ${money(chargeTotal)} inward charges in COGS`
              : `${entries} ${entries === 1 ? 'entry' : 'entries'}`
          }
        />
        <StatCard
          label={cmp.label}
          value={cmp.value}
          sub={cmp.sub}
          colour={cmp.colour}
        />
        <StatCard
          label="Biggest category"
          value={biggest ? biggest.category_name : '—'}
          sub={biggest ? money(biggest.total) : 'No spend in this period'}
        />
      </div>

      <WhereItWent
        rows={data.by_category}
        total={total}
        periodLabel={periodLabel}
      />

      {/* --- entries --- */}
      <div style={{
        background: C.card, border: `1px solid ${C.border}`,
        borderRadius: '12px', overflow: 'hidden',
      }}>
        <div style={{
          padding: '18px 22px', borderBottom: `1px solid ${C.borderSoft}`,
          display: 'flex', alignItems: 'flex-end', gap: '12px', flexWrap: 'wrap',
        }}>
          <div style={{ flex: '1 1 180px', minWidth: '160px' }}>
            <h3 style={{ margin: 0, fontSize: '16px', fontWeight: '700', color: C.text }}>
              Expenses
            </h3>
            <p style={{ margin: '3px 0 0', fontSize: '13px', color: C.textSoft }}>
              Running costs. Salaries posted from HR appear here too.
            </p>
          </div>

          <div style={{ width: '175px' }}>
            <Field label="Category">
              <select
                style={inputStyle}
                value={categoryId}
                onChange={e => setCategoryId(e.target.value)}
              >
                                <option value="">All categories</option>
                {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                <option value="inward">Inward charges</option>
              </select>
            </Field>
          </div>

          <PrintButton
            label="Print Report"
            filename={`Expense-Report-${window_.from_date}-to-${window_.to_date}`}
            disabled={loading || data.rows.length === 0}
            style={{ padding: '10px 18px', fontSize: 14 }}
            getConfig={() => expenseReportConfig({
              data,
              filters: { ...window_, category_id: categoryId },
              categories,
            })}
          />

          <Btn onClick={() => setForm(emptyForm())}>
            <Plus size={16} /> Add expense
          </Btn>
        </div>

        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '780px' }}>
            <thead>
              <tr>
                <th style={thStyle}>Date</th>
                <th style={thStyle}>Category</th>
                <th style={thStyle}>Paid to</th>
                <th style={thStyle}>Reference</th>
                <th style={thStyle}>Notes</th>
                <th style={{ ...thStyle, textAlign: 'right' }}>Amount</th>
                <th style={{ ...thStyle, textAlign: 'right' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr>
                  <td colSpan="7" style={{ ...tdStyle, textAlign: 'center', color: C.textMuted, padding: '36px' }}>
                    Loading…
                  </td>
                </tr>
              )}

                           {!loading && visibleRows.map(row => {
                const isInward = row.kind === 'inward';
                                    const fromHr     = row.kind === 'hr';
                    const fromSystem = row.kind === 'system';
                    const locked     = isInward || fromHr || fromSystem;

                return (
                  <tr key={row.key} style={{ background: locked ? C.rowAlt : 'transparent' }}>
                    <td style={{ ...tdStyle, color: C.textSoft, whiteSpace: 'nowrap' }}>
                      {fmtDate(row.date)}
                    </td>
                    <td style={{ ...tdStyle, fontWeight: '600' }}>
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: '7px' }}>
                        {row.category_name}
                        {row.location && (
                          <span
                            title={row.location === 'shop' ? 'Shop expense' : 'Factory expense'}
                            style={{
                              display: 'inline-flex', alignItems: 'center',
                              fontSize: '11px', fontWeight: '600',
                              padding: '2px 8px', borderRadius: '10px',
                              background: row.location === 'shop' ? '#dcfce7' : '#ede9fe',
                              color:      row.location === 'shop' ? '#15803d' : '#6d28d9',
                            }}
                          >
                            {row.location === 'shop' ? 'Shop' : 'Factory'}
                          </span>
                        )}
                        {fromHr && (
                          <span
                            title="Posted from the HR module"
                            style={{
                              display: 'inline-flex', alignItems: 'center', gap: '4px',
                              fontSize: '11px', fontWeight: '600',
                              padding: '2px 8px', borderRadius: '10px',
                              background: C.infoBg, color: C.info,
                            }}
                          >
                                                            <Lock size={10} /> HR
                              </span>
                            )}
                            {fromSystem && (
                              <span
                                title={row.source === 'roll_close'
                                  ? 'Posted when a jumbo roll was closed — reopen the roll to reverse it'
                                  : 'Posted automatically by the system'}
                                style={{
                                  display: 'inline-flex', alignItems: 'center', gap: '4px',
                                  fontSize: '11px', fontWeight: '600',
                                  padding: '2px 8px', borderRadius: '10px',
                                  background: C.rowAlt, color: C.textSoft,
                                  border: `1px solid ${C.border}`,
                                }}
                              >
                                <Lock size={10} /> {row.source === 'roll_close' ? 'Roll closed' : 'System'}
                              </span>
                            )}
                        {isInward && (
                          <span
                            title="Paid on a goods receipt. Already inside cost of goods — not deducted again in the P&L."
                            style={{
                              display: 'inline-flex', alignItems: 'center', gap: '4px',
                              fontSize: '11px', fontWeight: '600',
                              padding: '2px 8px', borderRadius: '10px',
                              background: '#fef3c7', color: '#d97706',
                            }}
                          >
                            <Truck size={10} /> In COGS
                          </span>
                        )}
                      </span>
                    </td>
                    <td style={tdStyle}>{row.paid_to || '—'}</td>
                    <td style={{ ...tdStyle, color: C.textSoft }}>{row.ref_no || '—'}</td>
                    <td style={{
                      ...tdStyle, color: C.textSoft, maxWidth: '220px',
                      overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                    }}>
                      {row.notes || '—'}
                    </td>
                    <td style={{ ...tdStyle, textAlign: 'right', fontWeight: '700' }}>
                      {money(row.amount)}
                    </td>
                    <td style={{ ...tdStyle, textAlign: 'right' }}>
                      {isInward ? (
                        <span style={{ fontSize: '12px', color: C.textMuted }}>On receipt</span>
                      ) : (
                        <div style={{ display: 'inline-flex', gap: '7px' }}>
                          <IconBtn
                            title={fromHr ? 'Posted from HR — edit it there' : fromSystem ? 'Posted automatically, cannot be changed' : 'Edit'}
                            disabled={fromHr || fromSystem}
                            onClick={() => openEdit(row)}
                          >
                            <Pencil size={15} />
                          </IconBtn>
                          <IconBtn
                            danger
                            title={fromHr ? 'Posted from HR — reverse it there' : fromSystem ? 'Posted automatically, cannot be changed' : 'Delete'}
                            disabled={fromHr || fromSystem}
                            onClick={() => remove(row)}
                          >
                            <Trash2 size={15} />
                          </IconBtn>
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}

              {!loading && visibleRows.length === 0 && (
                <tr>
                  <td colSpan="7" style={{ ...tdStyle, textAlign: 'center', color: C.textMuted, padding: '40px' }}>
                    {view.mode === 'month'
                      ? `No running costs recorded in ${monthLabel(view.month)}.`
                      : 'No running costs in this range.'}
                  </td>
                                </tr>
              )}

                            {!loading && showToggle && (
                <tr
                  onClick={() => setShowInward(o => !o)}
                  style={{ cursor: 'pointer', background: showInward ? '#fef3c7' : C.rowAlt }}
                >
                                    <td style={{ ...tdStyle, color: C.textSoft, whiteSpace: 'nowrap' }}>
                    {chargeSummary.date
                      ? (chargeSummary.sameDay ? fmtDate(chargeSummary.date) : `to ${fmtDate(chargeSummary.date)}`)
                      : '—'}
                  </td>
                  <td style={{ ...tdStyle, fontWeight: '600' }}>
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: '7px' }}>
                      <ChevronDown
                        size={15}
                        style={{
                          color: C.textMuted,
                          transform: showInward ? 'rotate(0deg)' : 'rotate(-90deg)',
                          transition: 'transform 0.15s',
                        }}
                      />
                      Inward charges
                      <span style={{
                        display: 'inline-flex', alignItems: 'center', gap: '4px',
                        fontSize: '11px', fontWeight: '600',
                        padding: '2px 8px', borderRadius: '10px',
                        background: '#fef3c7', color: '#d97706',
                      }}>
                        <Truck size={10} /> In COGS
                      </span>
                    </span>
                  </td>
                                    <td style={tdStyle}>{chargeSummary.vendor}</td>
                  <td style={{ ...tdStyle, color: C.textSoft }}>{chargeSummary.ref}</td>
                  <td style={{ ...tdStyle, color: C.textSoft }}>
                    {showInward ? 'Click to hide' : 'Click to show'}
                  </td>
                  <td style={{ ...tdStyle, textAlign: 'right', fontWeight: '700', whiteSpace: 'nowrap' }}>
                    {money(chargeTotal)}
                  </td>
                  <td style={{ ...tdStyle, textAlign: 'right' }}>
                    <span style={{ fontSize: '12px', color: C.textMuted }}>On receipt</span>
                  </td>
                </tr>
              )}
            </tbody>

            {/* Opened inward charges: one tinted block right under their
                header row, so they read as a group. */}
            {!loading && groupedCharges.length > 0 && (
              <tbody>
                {groupedCharges.map(row => (
                  <tr key={row.key} style={{ background: '#fffbeb' }}>
                    <td style={{ ...tdStyle, color: C.textSoft, whiteSpace: 'nowrap', borderLeft: '4px solid #f59e0b' }}>
                      {fmtDate(row.date)}
                    </td>
                    <td style={{ ...tdStyle, fontWeight: '600', paddingLeft: '44px' }}>
                      {row.category_name.replace(' (inward)', '')}
                    </td>
                    <td style={tdStyle}>{row.paid_to || '—'}</td>
                    <td style={{ ...tdStyle, color: C.textSoft }}>{row.ref_no || '—'}</td>
                    <td style={{
                      ...tdStyle, color: C.textSoft, maxWidth: '220px',
                      overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                    }}>
                      {row.notes || '—'}
                    </td>
                    <td style={{ ...tdStyle, textAlign: 'right', fontWeight: '700' }}>
                      {money(row.amount)}
                    </td>
                    <td style={{ ...tdStyle, textAlign: 'right' }}>
                      <span style={{ fontSize: '12px', color: C.textMuted }}>On receipt</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            )}

            {/* Two totals, never one. The first is deducted below
                gross profit; the second is already inside COGS. */}
                      {!loading && inwardOpen && (visibleRows.length > 0 || groupedCharges.length > 0) && (
              <tfoot>
                {showRunning && (
                  <tr>
                    <td colSpan="5" style={{
                      ...tdStyle, textAlign: 'right', fontWeight: '600',
                      color: C.textSoft, background: '#ecfdf5',
                    }}>
                      Running costs — deducted in the P&amp;L
                    </td>
                    <td style={{
                      ...tdStyle, textAlign: 'right', fontWeight: '700',
                      fontSize: '15px', background: '#ecfdf5',
                    }}>
                      {money(total)}
                    </td>
                    <td style={{ ...tdStyle, background: '#ecfdf5' }} />
                  </tr>
                )}
                {showCharges && chargeTotal > 0 && (
                  <tr>
                    <td colSpan="5" style={{
                      ...tdStyle, textAlign: 'right', fontWeight: '600',
                      color: C.textSoft, background: '#ecfdf5',
                    }}>
                      Inward charges — already in cost of goods
                    </td>
                    <td style={{
                      ...tdStyle, textAlign: 'right', fontWeight: '700',
                      fontSize: '15px', background: '#ecfdf5',
                    }}>
                      {money(chargeTotal)}
                    </td>
                    <td style={{ ...tdStyle, background: '#ecfdf5' }} />
                  </tr>
                )}
              </tfoot>
            )}
          </table>
        </div>
      </div>

                  {form && (
        <ExpenseForm
          form={form}
          setForm={setForm}
          categories={categories}
          onSave={save}
          onCancel={() => setForm(null)}
          saving={saving}
        />
      )}
    </div>
  );
};

export default ExpensesTab;